import { Restaurants, Voucher } from 'api/controllers/restaurants';
import { PagedList } from 'api/models';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    FormControl,
    Select,
    MenuItem,
    Button,
    Typography,
} from '@material-ui/core';
import EventIcon from '@material-ui/icons/Event';
import i18n from 'i18n-js';

import globalStore from 'store/globalStore';
import { formatDate } from 'helpers/datetimeHelper';
import FileUploadDnD from './FileUploadDnD';
import VoucherStatusBox from './VoucherStatusBox';
import { useAppSelector } from 'index';
import { VoucherStatus } from 'components/restaurantInformation/vouchers/components/VoucherStatusBox';
import { useStyles } from '../styles';
import { DateTimePicker } from 'components/shared';
import { pagingOptions } from 'constants/arrays';

export default function VouchersTab() {
    const styles = useStyles();
    const dispatch = useDispatch();
    const restaurant = useAppSelector((state) => state.restaurantDetails.restaurantInformation);
    const [vouchers, setVouchers] = useState<PagedList<Voucher>>({ list: [], count: 0 });
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(pagingOptions[0]);
    const [selectedStatus, setSelectedStatus] = useState<VoucherStatus | null>(null);
    const [prevStatus, setPrevStatus] = useState<VoucherStatus | null>(null);
    const [dateFrom, setDateFrom] = useState<Date | null>(null);
    const [dateTo, setDateTo] = useState<Date | null>(null);
    const [showDateFromPicker, setShowDateFromPicker] = useState(false);
    const [showDateToPicker, setShowDateToPicker] = useState(false);
    const [hasVouchers, setHasVouchers] = useState(false);

    const statuses = {
        [VoucherStatus.Active]: i18n.t('vouchers.active'),
        [VoucherStatus.Used]: i18n.t('vouchers.used'),
        [VoucherStatus.Expired]: i18n.t('vouchers.expired'),
    };

    const voucherStatuses = Object.values(VoucherStatus).filter(
        (v) => typeof v === 'string'
    ) as VoucherStatus[];

    const getStartOfDayTs = (value: Date | null) => {
        if (!value) return 0;
        const d = new Date(value);
        d.setHours(0, 0, 0, 0);
        return d.getTime();
    };

    const getEndOfDayTs = (value: Date | null) => {
        if (!value) return 0;
        const d = new Date(value);
        d.setHours(23, 59, 59, 999);
        return d.getTime();
    };

    const fetchVouchers = async (reset: boolean = false) => {
        dispatch(globalStore.actionCreators.showSpiner());
        try {
            const response = await Restaurants.GetVouchers(
                page,
                rowsPerPage,
                reset ? undefined : selectedStatus ? selectedStatus : undefined,
                reset ? 0 : getStartOfDayTs(dateFrom),
                reset ? 0 : getEndOfDayTs(dateTo)
            );
            if (response.count > 0 && !hasVouchers) {
                setHasVouchers(true);
            }
            setVouchers(response);
        } catch (error) {
            dispatch(globalStore.actionCreators.showToaster('error', error));
        } finally {
            dispatch(globalStore.actionCreators.hideSpiner());
        }
    };

    useEffect(() => {
        fetchVouchers();
    }, [page, rowsPerPage]);

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newRowsPerPage = +event.target.value;
        setRowsPerPage(newRowsPerPage);
        setPage(0);
    };

    const handleStatusChange = (event: any) => {
        setPrevStatus(selectedStatus);
        setSelectedStatus(event.target.value || null);
        setPage(0);
    };

    const formatTimestamp = (timestamp: number) => {
        if (!timestamp || timestamp === 0) return '-';
        return formatDate(new Date(timestamp), '.', true);
    };

    function formatValue(value: string) {
        const numericValue = parseFloat(value);
        if (isNaN(numericValue)) return value;
        return `${restaurant.paymentDetails.currency || 'CHF'} ${numericValue.toFixed(2)}`;
    }

    function closeDatePickers() {
        setShowDateFromPicker(false);
        setShowDateToPicker(false);
    }

    async function uploadVouchers(file: File) {
        dispatch(globalStore.actionCreators.showSpiner());
        try {
            const result = await Restaurants.ImportVouchers(file);

            if (!!result) {
                dispatch(globalStore.actionCreators.hideSpiner());
                dispatch(globalStore.actionCreators.showToaster('warning', result));
            } else {
                dispatch(
                    globalStore.actionCreators.showToaster(
                        'success',
                        i18n.t('vouchers.uploadSuccess')
                    )
                );
            }
            fetchVouchers();
        } catch (error) {
            dispatch(globalStore.actionCreators.showToaster('error', error));
        } finally {
            dispatch(globalStore.actionCreators.hideSpiner());
        }
    }

    function resetFilters() {
        setSelectedStatus(null);
        setPrevStatus(null);
        setDateFrom(null);
        setDateTo(null);
        fetchVouchers(true);
    }

    const isButtonDisabled = () => !!!dateFrom && !!!dateTo && selectedStatus === prevStatus;

    return (
        <Paper
            style={{
                height: 'calc(100vh - 70px)',
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                overflow: 'hidden',
            }}
        >
            <FileUploadDnD onUpload={uploadVouchers} />
            {!hasVouchers ? (
                <Typography style={{ textAlign: 'center' }}>
                    {i18n.t('vouchers.noVouchersUploaded')}
                </Typography>
            ) : (
                <>
                    <div style={{ padding: '16px', borderBottom: '1px solid #e0e0e0' }}>
                        <FormControl
                            style={{ minWidth: 150 }}
                            variant="outlined"
                            className={styles.selectRoot}
                        >
                            <Select
                                value={selectedStatus || ''}
                                onChange={handleStatusChange}
                                displayEmpty
                            >
                                <MenuItem value="">{i18n.t('vouchers.allStatuses')}</MenuItem>
                                {voucherStatuses.map((status) => (
                                    <MenuItem key={status} value={status}>
                                        {statuses[status].charAt(0).toUpperCase() +
                                            statuses[status].slice(1).toLowerCase()}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl variant="outlined">
                            <Button
                                variant="outlined"
                                className={styles.buttonRoot}
                                startIcon={<EventIcon color="secondary" fontSize="small" />}
                                onClick={() => setShowDateFromPicker(true)}
                            >
                                {dateFrom
                                    ? formatDate(dateFrom, '.', true)
                                    : i18n.t('vouchers.from')}
                            </Button>
                            {showDateFromPicker && (
                                <DateTimePicker
                                    showButtons={true}
                                    isDateRange={false}
                                    hideTimeSelect={true}
                                    date={dateFrom || new Date()}
                                    onSubmit={(date) => {
                                        setDateFrom(date);
                                    }}
                                    isOpen={showDateFromPicker}
                                    onClose={() => {
                                        setTimeout(() => {
                                            closeDatePickers();
                                        }, 0);
                                    }}
                                />
                            )}
                        </FormControl>
                        <FormControl variant="outlined">
                            <Button
                                variant="outlined"
                                className={styles.buttonRoot}
                                startIcon={<EventIcon color="secondary" fontSize="small" />}
                                onClick={() => setShowDateToPicker(true)}
                            >
                                {dateTo ? formatDate(dateTo, '.', true) : i18n.t('vouchers.to')}
                            </Button>
                            {showDateToPicker && (
                                <DateTimePicker
                                    showButtons={true}
                                    isDateRange={false}
                                    hideTimeSelect={true}
                                    date={dateTo || new Date()}
                                    onSubmit={(date) => {
                                        setDateTo(date);
                                    }}
                                    isOpen={showDateToPicker}
                                    onClose={() => {
                                        setTimeout(() => {
                                            closeDatePickers();
                                        }, 0);
                                    }}
                                />
                            )}
                        </FormControl>
                        <FormControl variant="outlined">
                            <Button
                                variant="contained"
                                color="primary"
                                className={styles.filterButton}
                                onClick={() => fetchVouchers()}
                                disabled={isButtonDisabled()}
                            >
                                {i18n.t('vouchers.filter')}
                            </Button>
                        </FormControl>
                        <FormControl variant="outlined">
                            <Button
                                variant="outlined"
                                color="primary"
                                className={styles.filterButton}
                                onClick={resetFilters}
                                disabled={isButtonDisabled()}
                            >
                                {i18n.t('vouchers.reset')}
                            </Button>
                        </FormControl>
                    </div>

                    <TableContainer
                        style={{
                            flex: 1,
                            minHeight: 0,
                            overflowX: 'auto',
                            overflowY: 'auto',
                        }}
                    >
                        <Table
                            stickyHeader
                            aria-label="vouchers table"
                            className={styles.vouchersTableRoot}
                        >
                            <TableHead>
                                <TableRow>
                                    <TableCell>{i18n.t('vouchers.voucherId')}</TableCell>
                                    <TableCell>{i18n.t('vouchers.value')}</TableCell>
                                    <TableCell>{i18n.t('vouchers.expirationDate')}</TableCell>
                                    <TableCell>{i18n.t('vouchers.status')}</TableCell>
                                    <TableCell>{i18n.t('vouchers.useDate')}</TableCell>
                                    <TableCell style={{ paddingBottom: 16, paddingTop: 16 }}>
                                        {i18n.t('vouchers.uploadDate')}
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {vouchers?.list?.length ? (
                                    vouchers.list.map((voucher) => (
                                        <TableRow key={voucher.id}>
                                            <TableCell>{voucher.voucherId}</TableCell>
                                            <TableCell>{formatValue(voucher.value)}</TableCell>
                                            <TableCell>
                                                {formatTimestamp(voucher.expirationDate)}
                                            </TableCell>
                                            <TableCell>
                                                <VoucherStatusBox status={voucher.status} />
                                            </TableCell>
                                            <TableCell>
                                                {formatTimestamp(voucher.useDate)}
                                            </TableCell>
                                            <TableCell>
                                                {formatTimestamp(voucher.uploadDate)}
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center">
                                            {i18n.t('vouchers.noVouchersFound')}
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <TablePagination
                        rowsPerPageOptions={pagingOptions}
                        component="div"
                        count={vouchers.count}
                        rowsPerPage={rowsPerPage}
                        labelRowsPerPage={i18n.t('common.rowsPerPage') || 'Rows per page:'}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                </>
            )}
        </Paper>
    );
}
